import { useState } from "react";
import { useTenant } from "../../../Contexts/TenantContext";
import { supportService } from "../../../Api/Services/supportService";
import { formatPhoneNumber, formatPostalCode } from "../../../Utils/DataFormatters";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import SocialMediaIcons from "../../../Components/SocialMediaIcons/SocialMediaIcons";
import Footer from "../../../Components/Footer/Footer";
import Button from "../../../Components/Button/Button";
import PublicPageTitle from "../../../Components/PublicPageTitle/PublicPageTitle";

const UserContactPage = () => {
  const { tenantContact, tenantBranches, loading } = useTenant();

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitting(true);
    try {
      const submitted = await supportService.submitContactForm(formData);
      if (submitted) {
        toast.success("Email enviado com sucesso!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: ""
        });
      }      
    } catch (err) {
      toast.error("Não foi possível enviar o email, tente novamente mais tarde!");
    } finally {
      setSubmitting(false);
    }
  };

  const SkeletonBlock = ({ height = 16, width = "100%" }: { height?: number | string; width?: number | string }) => (
    <div className="animate-pulse bg-gray-200 rounded w-full" style={{ height, maxWidth: width }} />
  );

  const renderSkeleton = () => (
    <div className="mb-10 page-gutter pt-16 md:pt-[7.5rem] pb-4 md:pb-20">
      <SkeletonBlock height={32} width={220} />
      <div className="w-full flex justify-between gap-10 flex-col md:flex-row mt-10">
        <div className="border border-gray-200 border-t-4 border-t-black flex flex-col w-full md:w-1/2 p-8 md:p-12 space-y-4">
          <SkeletonBlock height={20} width={180} />
          <SkeletonBlock height={14} width={260} />
          <SkeletonBlock height={14} width={220} />

          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="space-y-2">
              <SkeletonBlock height={14} width={120} />
              <SkeletonBlock height={40} />
            </div>
          ))}
          <SkeletonBlock height={44} />
        </div>

        <div className="w-full md:w-1/2 space-y-6">
          <div className="space-y-2">
            <SkeletonBlock height={20} width={140} />
            <SkeletonBlock height={16} width={320} />
          </div>

          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="space-y-2">
                <SkeletonBlock height={14} width={200} />
                <SkeletonBlock height={16 + (index % 2) * 4} />
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <SkeletonBlock height={20} width={120} />
            <div className="flex space-x-3 text-2xl text-gray-500">
              {Array.from({ length: 4 }).map((_, index) => (
                <SkeletonBlock key={index} height={32} width={32} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <SmartNavbar />
      {loading ? (
        renderSkeleton()
      ) : (
        <div className={`mb-10 page-gutter pt-16 md:pt-[7.5rem] pb-4 md:pb-20`}>
          <PublicPageTitle className="mb-10 md:mb-14">{"CONTATO"}</PublicPageTitle>
          <div className={`w-full flex justify-between gap-10 flex-col md:flex-row `}>
            <div className={`flex flex-col w-full md:w-1/2 p-8 md:p-12 border border-gray-200 border-t-4 border-t-black`}>
              <h2 className={`font-semibold text-center text-base sm:text-lg text-gray-600`}>{"ATENDIMENTO POR E-MAIL"}</h2>
              <p className={`my-2 text-xs text-gray-600`}>
                {"Envie sua mensagem diretamente para nossa equipe através do formulário abaixo. Você receberá a resposta em seu e-mail informado."}
              </p>
              <p className={`mt-1 mb-2 text-xs text-gray-600`}>
                {"Se preferir, entre em contato direto através do e-mail:"} {tenantContact?.email}
              </p>

              <form className="w-full space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-sm text-gray-700 font-medium mb-1">{"Nome"}</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={`w-full border border-gray-300 px-3 py-3 focus:outline-none text-xs rounded-sm`}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-700 font-medium mb-1">{"E-mail"}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`w-full border border-gray-300 px-3 py-3 focus:outline-none text-xs rounded-sm`}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-700 font-medium mb-1">{"Telefone"}</label>
                  <input
                    type="text"
                    name="phone"
                    value={formatPhoneNumber(formData.phone)}
                    onChange={handleChange}
                    required
                    className={`w-full border border-gray-300 px-3 py-3 focus:outline-none text-xs rounded-sm`}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-700 font-medium mb-1">{"Assunto"}</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className={`w-full border border-gray-300 px-3 py-3 focus:outline-none text-xs rounded-sm`}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-700 font-medium mb-1">{"Mensagem"}</label>
                  <textarea
                    rows={5}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className={`w-full border border-gray-300 px-3 py-3 focus:outline-none max-h-96 text-xs rounded-sm`}
                  ></textarea>
                </div>

                <div>
                  <Button isLoading={submitting}
                    type="submit"
                    disabled={submitting}
                    className="w-full py-2"
                  >
                    {"ENVIAR"}
                  </Button>
                </div>
              </form>
            </div>
            <div className="w-full md:w-1/2">
              {tenantContact?.street && tenantContact?.number && tenantContact?.neighborhood && tenantContact?.city && tenantContact?.state && tenantContact?.postalCode &&
                <div>
                  <h4 className={`font-semibold text-lg mb-4 text-primary-600`}>
                    {tenantBranches?.length > 0 ? "ENDEREÇOS" : "ENDEREÇO"}
                  </h4>
                  <p className="text-gray-600">
                    {`${tenantContact?.street}, ${tenantContact?.number} - ${tenantContact?.neighborhood}, ${tenantContact?.city}/${tenantContact?.state} - ${formatPostalCode(tenantContact?.postalCode)}`}
                  </p>
                  {tenantBranches?.map(branch => (
                    <p key={branch.id} className="text-gray-600 mt-2">
                      {branch.label && <span className="font-semibold">{branch.label} — </span>}
                      {`${branch.street}, ${branch.number} - ${branch.neighborhood}, ${branch.city}/${branch.state} - ${formatPostalCode(branch.postalCode)}`}
                    </p>
                  ))}
                </div>
              }
              <div>
                {(tenantContact?.phoneNumber || tenantContact?.whatsAppNumber) &&
                  <div>
                    <hr className="my-7" />
                    <h4 className={`font-semibold text-lg mb-4 text-primary-600`}>{"TELEFONES"}</h4>
                  </div>
                }

                {tenantContact?.phoneNumber &&
                  <div className="mb-4">
                    <p className="text-gray-600 font-semibold mb-2">{"Contato"}</p>
                    <p className="text-gray-600">{formatPhoneNumber(tenantContact.phoneNumber)}</p>
                  </div>
                }

                {tenantContact?.whatsAppNumber &&
                  <div>
                    <p className="text-gray-600 font-semibold mb-2">WhatsApp</p>
                    <p className="text-gray-600">{formatPhoneNumber(tenantContact.whatsAppNumber)}</p>
                  </div>
                }

              </div>

              {tenantContact?.email && 
                <div>
                  <hr className="my-7" />

                  <h4 className="text-primary-600 font-semibold text-lg mb-4">E-MAIL</h4>
                  <p className="text-gray-600">{tenantContact.email}</p>
                </div>
              }

              {(
                tenantContact?.facebookUrl || 
                tenantContact?.instagramUrl || 
                tenantContact?.youTubeUrl || 
                tenantContact?.linkedInUrl
              ) &&
                <div>
                  <hr className="my-7" />

                  <h4 className="text-primary-600 font-semibold text-lg mb-4">{"REDES SOCIAIS"}</h4>
                  <SocialMediaIcons className="flex space-x-3 text-2xl text-gray-500" />
                </div>
              }
            </div>
          </div>        
        </div>
      )}
      <Footer />
    </div>
  );
};

export default UserContactPage;
